import React from 'react';
import { RotateCcw, ShieldCheck, ReceiptIndianRupee, CircleHelp } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import { SUPPORT_EMAIL, SUPPORT_EMAIL_MAILTO } from '@/config/links';

const ReturnRefund = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Return & Refund Policy"
        description="Return and refund policy for Boxd Logistics services and platform."
        canonical="https://boxdlogistics.in/return-refund"
        robots="noindex, follow"
      />
      <Navbar />

      <section className="relative pt-32 pb-16 px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
            backgroundSize: '48px 48px'
          }}
        ></div>
        <div className="absolute -top-12 right-0 w-72 h-72 bg-gradient-to-br from-blue-300/20 to-blue-700/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 text-blue-900 px-4 py-2 text-sm font-semibold mb-6">
            <RotateCcw className="w-4 h-4" />
            Return and Refund
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">Return & Refund Policy</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl">
            Your satisfaction is our priority. This policy explains how return requests and refunds are handled at Boxd Logistics.
          </p>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto space-y-10">
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Return Policy</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              If you wish to return a shipment, please review the following guidelines:
            </p>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-blue-800 mt-0.5" />
                <span>
                  <strong>Shipment Condition:</strong> Ensure that the shipment is in its original condition and packaging.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-blue-800 mt-0.5" />
                <span>
                  <strong>Return Request:</strong> Submit a return request within 7 days of receiving the shipment.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-blue-800 mt-0.5" />
                <span>
                  <strong>Reason for Return:</strong> Provide a valid reason for the return, such as damaged goods, incorrect delivery, or other valid concerns.
                </span>
              </li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-6">
              Once your return request is approved, our team will guide you through the return process. Please note that some items may not be eligible for return, and specific conditions may apply.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-blue-50/30 p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Refund Policy</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              We aim to process refunds promptly and efficiently. Here are the key points of our refund policy:
            </p>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-start gap-3">
                <ReceiptIndianRupee className="w-5 h-5 text-blue-800 mt-0.5" />
                <span>
                  <strong>Eligibility:</strong> Refunds are issued for eligible returns that meet our criteria.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <ReceiptIndianRupee className="w-5 h-5 text-blue-800 mt-0.5" />
                <span>
                  <strong>Payment Method:</strong> Refunds are typically processed using the original payment method used for the purchase.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <ReceiptIndianRupee className="w-5 h-5 text-blue-800 mt-0.5" />
                <span>
                  <strong>Processing Time:</strong> Please allow 15 days for the refund to be credited after the return is received and approved.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <ReceiptIndianRupee className="w-5 h-5 text-blue-800 mt-0.5" />
                <span>
                  <strong>Deductions:</strong> In some cases, deductions may apply to cover shipping and handling costs or restocking fees.
                </span>
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have any questions or need assistance with a return or refund, please contact our customer support team. We are here to help and ensure your satisfaction.
            </p>
            <div className="mt-4">
              <a href={SUPPORT_EMAIL_MAILTO} className="text-blue-800 hover:text-blue-900 font-semibold">
                {SUPPORT_EMAIL}
              </a>
            </div>
          </div>

          <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-8 shadow-sm">
            <div className="flex items-start gap-3">
              <CircleHelp className="w-5 h-5 text-blue-900 mt-0.5" />
              <p className="text-gray-700 leading-relaxed">
                <strong>Additional Information:</strong> This policy is subject to change without notice. Please review this page periodically for updates. If you have special requests regarding returns or refunds, our support team will do its best to assist.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReturnRefund;
